
import { useState, useEffect } from "react";
import { Product } from "@/lib/types";
import { addUsageRecord, generateId } from "@/lib/storage";
import { useToast } from "@/hooks/use-toast";
import { fetchProductsFromApi } from "@/lib/storage/product/api";
import { supabase } from "@/integrations/supabase/client";
import { ProductQuantity } from "@/components/group/GroupProductQuantities";

export interface UseUsageTrackingProps {
  groups: any[];
  products: Product[];
  setProducts: (products: Product[]) => void;
}

export const useUsageTracking = ({ groups, products, setProducts }: UseUsageTrackingProps) => {
  const { toast } = useToast();
  const [defaultQuantity, setDefaultQuantity] = useState<number>(1);
  const [groupProductQuantities, setGroupProductQuantities] = useState<Record<string, ProductQuantity[]>>({});

  // Load saved product quantities for all groups
  useEffect(() => {
    const loadGroupQuantities = async () => {
      if (groups.length === 0) return;
      
      try {
        const { data, error } = await supabase
          .from('group_product_quantities')
          .select('group_id, product_id, quantity');
          
        if (error) throw error;
        
        if (data && data.length > 0) {
          const quantitiesByGroup: Record<string, ProductQuantity[]> = {};
          
          data.forEach(item => {
            if (!quantitiesByGroup[item.group_id]) {
              quantitiesByGroup[item.group_id] = [];
            }
            
            quantitiesByGroup[item.group_id].push({
              productId: item.product_id,
              quantity: item.quantity
            });
          });
          
          setGroupProductQuantities(quantitiesByGroup);
          console.log("Loaded group quantities:", quantitiesByGroup);
        }
      } catch (err) {
        console.error("Error loading group product quantities:", err);
      }
    };
    
    loadGroupQuantities();
  }, [groups]);

  const getProductQuantities = (groupId: string): ProductQuantity[] => {
    return groupProductQuantities[groupId] || [];
  };

  const handleRecordUsage = (groupId: string, customQuantities?: Record<string, number>, usageDate?: Date) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    
    const groupProducts = products.filter(product => group.productIds.includes(product.id));
    
    if (groupProducts.length === 0) {
      toast({
        title: "No products",
        description: "This group doesn't contain any products to record usage for.",
      });
      return;
    }
    
    try {
      // Record usage for each product in the group
      let recordedCount = 0;
      let totalItems = 0;
      
      // Get saved quantities for this group
      const savedQuantities = getProductQuantities(groupId);
      console.log("Using saved quantities for group:", groupId, savedQuantities);
      
      // Use the provided date or default to current date
      const recordDate = usageDate || new Date();
      
      groupProducts.forEach(product => {
        // Determine quantity in this priority:
        // 1. Custom quantity from dialog if provided
        // 2. Saved quantity from database
        // 3. Default quantity (1)
        const customQty = customQuantities?.[product.id];
        const savedQty = savedQuantities.find(q => q.productId === product.id)?.quantity;
        
        const quantity = (customQty !== undefined) 
          ? customQty 
          : (savedQty !== undefined)
            ? savedQty
            : defaultQuantity;
        
        console.log(`Product ${product.name}: custom=${customQty}, saved=${savedQty}, using=${quantity}`);
        
        if (quantity > 0) {
          const usageRecord = {
            id: generateId(),
            productId: product.id,
            quantity: quantity,
            date: recordDate,
          };
          
          addUsageRecord(usageRecord);
          recordedCount++;
          totalItems += quantity;
        }
      });
      
      // Refresh products to update stock counts
      fetchProductsFromApi(true).then(setProducts);
      
      toast({
        title: "Usage Recorded",
        description: `Used ${totalItems} items from ${recordedCount} products in ${group.name}`,
      });
    } catch (error) {
      console.error("Error recording usage:", error);
      toast({
        title: "Error",
        description: "Failed to record usage. Please try again.",
        variant: "destructive",
      });
    }
  };

  return { 
    handleRecordUsage,
    defaultQuantity,
    setDefaultQuantity,
    getProductQuantities
  };
};
